import { useLocalObservable } from 'mobx-react-lite';
import { ElementType, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IcAppearanceIcon,
  IcDevicesIcon,
  IcGlobeIcon,
  IcInfoIcon,
  IcLogOutIcon,
  IcSaveStrokeIcon,
} from '@gem/icons';
import { UserProfileUsernameBadge } from '@gem/models/userModel';
import { useTranslation } from '@gem/modules/i18n';
import { language } from '@gem/modules/storage';
import { PopupStore, ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { IUserNameBadgeIcon, mapUserProfileBadgeToIcon } from 'APP/constants/userBadges';
import { AboutPopup } from 'APP/main/PopupManager/Popups/AboutPopup/AboutPopup';
import { AppearanceSettingsPopup } from 'APP/main/PopupManager/Popups/AppearanceSettingsPopup/AppearanceSettingsPopup';
import { EditProfilePopup } from 'APP/main/PopupManager/Popups/EditProfilePopup/EditProfilePopup';
import { LanguageChangePopup } from 'APP/main/PopupManager/Popups/LanguageChangePopup/LanguageChangePopup';
import { SessionsPopup } from 'APP/main/PopupManager/Popups/Sessions/SessionsPopup/SessionsPopup';
import { RouterLink } from 'APP/router/constants';
import Entities from 'STORE';
import { User } from 'STORE/Users/User/User';

interface IMenuItem {
  key: string;
  title: string;
  Icon: ElementType;
  onClick(): void;
  count?: number | string;
  suffix?: string;
}

interface IDivider {
  key: string;
  isDivider: true;
}

type IMenu = IMenuItem | IDivider;

export function isDivider(menuItem: IMenu): menuItem is IDivider {
  return (menuItem as IDivider).isDivider;
}

interface IMyProfilePresenter {
  childPopup: PopupStore | null;
  me: User;
  userNameBadge: IUserNameBadgeIcon | null;
  menuItems: (IMenuItem | IDivider)[];
  sessionsCount: number | undefined;
  openEditProfile(): void;
  openSavedMessagesChat(): Promise<void>;
  openAppearance(): void;
  openAbout(): void;
  logout(): void;
  openChildPopup(component: FC<ICommonPopupsProps>): void;
  openDevices(): void;
  onOpenLanguageChange(): void;
}

export function useMyProfilePresenter(popupInstance?: PopupStore): IMyProfilePresenter {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const presenter = useLocalObservable<IMyProfilePresenter>(() => ({
    childPopup: null,

    get me(): User {
      return Entities.usersStore.Me;
    },

    openEditProfile(): void {
      presenter.openChildPopup(EditProfilePopup);
    },

    get userNameBadge(): IUserNameBadgeIcon | null {
      return presenter.me?.usernameBadge
        ? mapUserProfileBadgeToIcon[presenter.me?.usernameBadge as UserProfileUsernameBadge] || null
        : null;
    },

    get sessionsCount(): number {
      return Entities.sessionsStore.sessions.length + 1;
    },

    get menuItems(): IMenu[] {
      return [
        {
          key: 'saved-messages',
          Icon: IcSaveStrokeIcon,
          title: t('saved_messages'),
          onClick: presenter.openSavedMessagesChat,
        },
        {
          key: 'appearance',
          Icon: IcAppearanceIcon,
          title: t('chat_appearance'),
          onClick: presenter.openAppearance,
        },
        {
          key: 'devices',
          Icon: IcDevicesIcon,
          title: t('common_devices'),
          onClick: presenter.openDevices,
          count: presenter.sessionsCount,
        },
        {
          key: 'about',
          Icon: IcInfoIcon,
          title: t('about_gem4me'),
          onClick: presenter.openAbout,
        },
        {
          key: 'language',
          title: t('admin_ui:language_title'),
          suffix: language.get()?.toUpperCase(),
          onClick: presenter.onOpenLanguageChange,
          Icon: IcGlobeIcon,
        },
        {
          key: 'divider',
          isDivider: true,
        },
        {
          key: 'logout',
          Icon: IcLogOutIcon,
          title: t('settings_activity_exit'),
          onClick: presenter.logout,
        },
      ];
    },

    async openSavedMessagesChat(): Promise<void> {
      Tasks.group.goToSavedMessages();
      popupInstance?.close();
    },

    openAppearance(): void {
      presenter.openChildPopup(AppearanceSettingsPopup);
    },

    openAbout(): void {
      presenter.openChildPopup(AboutPopup);
    },

    openDevices(): void {
      presenter.openChildPopup(SessionsPopup);
    },

    onOpenLanguageChange(): void {
      presenter.openChildPopup(LanguageChangePopup);
    },

    logout(): void {
      Tasks.app.showConfirm({
        text: t('exit_alert'),
        async onSuccess(): Promise<void> {
          await Tasks.authorization.logout(true);
          navigate(RouterLink.Login, { replace: true });
        },
      });
    },

    openChildPopup(component: FC<ICommonPopupsProps>): void {
      popupInstance?.hide();

      presenter.childPopup = Tasks.app.openPopup(component, {
        onClose: () => popupInstance?.close(),
        onBack: () => popupInstance?.show(),
      });
    },
  }));

  useEffect(() => {
    Tasks.userSettings.getSessionsInfo();

    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
}
